// The Summary reports' client-side work: the Filter panel, the section sums and the unit picked.
import { EMPTY_LIST } from '@/lib/utils';
import { MASS_BALANCE_FIGURES, MOVEMENT_SECTIONS, signed } from '../mappers/inventoryReportMappers';

// Figma 4196:133937 — the GHG savings brackets; each is [min, max), the top one closed at 100.
export const GHG_SAVINGS_RANGES = Object.freeze([
  { value: '95-100', min: 95, max: 100 },
  { value: '90-95', min: 90, max: 95 },
  { value: '80-90', min: 80, max: 90 },
  { value: '70-80', min: 70, max: 80 },
  { value: '60-70', min: 60, max: 70 },
  { value: '45-60', min: 45, max: 60 },
  { value: 'below-45', min: null, max: 45 },
]);

const isInRange = (savings, range) => {
  if (savings == null) return false;
  if (range.min == null) return savings < range.max;
  return savings >= range.min && (range.max === 100 ? savings <= 100 : savings < range.max);
};

export const SUMMARY_FILTER_FIELDS = Object.freeze({
  ghgSavings: 'ghgSavings',
  ghgEmissions: 'ghgEmissions',
  countryOfOrigin: 'countryOfOrigin',
  rawMaterialType: 'rawMaterialType',
});

const picked = (values) => (Array.isArray(values) && values.length > 0 ? values : null);

/** Whether the Filter panel narrows anything; an empty group never does. */
export const hasSummaryFilters = (filters = {}) => Object.values(filters).some(picked);

// Groups AND together; the boxes ticked inside one group OR.
const matchesFilters = (row, filters = {}) => {
  const savings = picked(filters[SUMMARY_FILTER_FIELDS.ghgSavings]);
  if (
    savings &&
    !GHG_SAVINGS_RANGES.some(
      (range) => savings.includes(range.value) && isInRange(row.ghgSavingsRaw, range),
    )
  ) {
    return false;
  }
  const listMatch = (field, key) => {
    const values = picked(filters[field]);
    return !values || values.includes(String(row[key]));
  };
  return (
    listMatch(SUMMARY_FILTER_FIELDS.ghgEmissions, 'ghgEmissionsRaw') &&
    listMatch(SUMMARY_FILTER_FIELDS.countryOfOrigin, 'countryOfOriginRaw') &&
    listMatch(SUMMARY_FILTER_FIELDS.rawMaterialType, 'rawMaterialTypeRaw')
  );
};

export const filterSummaryRows = (rows = EMPTY_LIST, filters = {}) =>
  hasSummaryFilters(filters) ? rows.filter((row) => matchesFilters(row, filters)) : rows;

// The same rule as iscc-backend's sumLeg: unknown amounts are skipped, all-unknown stays unknown.
const sumOf = (amounts) => {
  const known = amounts.filter((amount) => amount != null);
  return known.length === 0 ? null : known.reduce((total, amount) => total + amount, 0);
};

const numbered = (rows) => rows.map((row, index) => ({ ...row, serialNo: String(index + 1) }));

/** Mass balance: the rows shown, and their totals — the server's own while nothing is filtered. */
export const buildMassBalanceView = (report, filters) => {
  const rows = filterSummaryRows(report?.rows, filters);
  const totals = hasSummaryFilters(filters)
    ? Object.fromEntries(
        MASS_BALANCE_FIGURES.map((key) => [key, sumOf(rows.map((row) => row.figures[key]))]),
      )
    : (report?.totals ?? {});
  const formatFigures = (figures) =>
    Object.fromEntries(MASS_BALANCE_FIGURES.map((key) => [key, signed(figures?.[key])]));
  return {
    rows: numbered(rows.map((row) => ({ ...row, ...formatFigures(row.figures) }))),
    totals: formatFigures(totals),
  };
};

// One column per period plus the grand total, each in the unit picked.
const movementCells = (periods, amountsOf) => ({
  ...Object.fromEntries(periods.map((period) => [period.id, signed(amountsOf(period))])),
  grandTotal: signed(amountsOf(null)),
});

/**
 * Movement ▸ Summary: inbound rows then outbound rows, numbered within each, a signed subtotal
 * per section and the net total — the same sums iscc-backend's withMovementSectionTotals makes.
 */
export const buildMovementSummaryView = (report, unitType, filters) => {
  const periods = report?.periods ?? EMPTY_LIST;
  const rows = filterSummaryRows(report?.rows, filters);
  const legOf = (row) => row.amounts[unitType];
  const amountOf = (row, period) =>
    period ? (legOf(row).periods[period.index] ?? null) : legOf(row).grandTotal;
  const totalOf = (sectionRows) =>
    movementCells(periods, (period) => sumOf(sectionRows.map((row) => amountOf(row, period))));

  const sectionRows = (section) => rows.filter((row) => row.section === section);
  const inbound = sectionRows(MOVEMENT_SECTIONS.inbound);
  const outbound = sectionRows(MOVEMENT_SECTIONS.outbound);

  return {
    periods,
    rows: [...numbered(inbound), ...numbered(outbound)].map((row) => ({
      ...row,
      ...movementCells(periods, (period) => amountOf(row, period)),
    })),
    subtotals: {
      [MOVEMENT_SECTIONS.inbound]: totalOf(inbound),
      [MOVEMENT_SECTIONS.outbound]: totalOf(outbound),
    },
    total: totalOf(rows),
  };
};

/** Movement ▸ Detailed: one row per record, its quantity in the unit picked. */
export const buildMovementDetailedView = (report, unitType, filters) =>
  numbered(filterSummaryRows(report?.detailed, filters)).map((row) => ({
    ...row,
    quantityText: signed(row.quantity[unitType]),
  }));
